/**
 * SAVED VIEWS (display P7, migration 081): a person's named display configurations for
 * one sheet — the bar's "Views" menu, the "Save as view…" sheet, and the reads/writes that
 * feed them. One hook, three screens (outfits/boxes/items), so the walk is written once.
 *
 * PERSONAL by construction (maintainer ruling, 30 Aug 2026): the server writes `owner_id`
 * from the seat, RLS shows only mine. LOCAL-ONLY is the degrade: on a database without
 * 081 (`available: false`) the menu hides and the panel's act says so — the localStore
 * layer (display-state.ts) keeps carrying the CURRENT unsaved view as it always did.
 *
 * A stored config is re-sanitised against the CURRENT spec on apply (`sanitizeConfig`):
 * a view saved before an axis was renamed falls back to the default rather than breaking
 * the sheet — the migration header's contract with the schema.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { api, call } from '../platform/api.js';
import { Sheet } from '../components/sheet.js';
import { useConfirm } from '../components/confirm.js';
import { effectiveConfig, sanitizeConfig, type DisplayConfig, type EntityDisplaySpec } from './engine.js';
import { useDismiss } from './display-bar.js';
import type { SavedView, SavedViewsPayload, ViewScreen } from '../../../api/src/catalog/saved-views.js';

export function useSavedViews<Row>({
  screen,
  spec,
  fallback,
  config,
  enabled,
  onApply,
  notify,
}: {
  screen: ViewScreen;
  spec: EntityDisplaySpec<Row>;
  fallback: DisplayConfig;
  /** The sheet's current config — what "Save as view…" captures. */
  config: DisplayConfig;
  /** Desk only: the phone never reads views (its lists are not on the engine). */
  enabled: boolean;
  onApply: (config: DisplayConfig) => void;
  /** The screen's own status line — saves and deletes say what happened (G10). */
  notify: (line: string) => void;
}): { menu: ReactNode; openSave: () => void; sheet: ReactNode } {
  const [views, setViews] = useState<SavedViewsPayload | null>(null);
  const [open, setOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [confirmEl, ask] = useConfirm();
  const menuRef = useDismiss(open, () => setOpen(false));

  useEffect(() => {
    if (!enabled) return;
    void (async () => {
      const r = await call('listSavedViews', () => api.listSavedViews.query({ screen }));
      if (r.ok) setViews(r.data);
      // A failed read leaves the menu absent rather than the sheet broken — a preference.
    })();
  }, [enabled, screen]);

  const available = views?.available === true;

  /**
   * THE ACTIVE VIEW IS DERIVED, NEVER STORED (0.1.455, maintainer 3 Sep: "I want the active view
   * to show in this button so that I can see which view is active"). A view is active exactly
   * while the sheet's current config EQUALS its saved config (sanitised against today's spec,
   * the same way `apply` reads it) — so a level added after applying it makes the button say
   * "Views" again, and a view re-created by hand lights up without anyone applying it. A stored
   * "current view id" would lie in both cases.
   */
  // Compared WITHOUT the sort-intent flag (0.1.487): a view saved before the flag existed and
  // the same view re-chosen by hand are one view — the rows are in the same order either way.
  const configKey = JSON.stringify(effectiveConfig(config));
  const active = views?.views.find((v) => JSON.stringify(effectiveConfig(sanitizeConfig(v.config, spec, fallback))) === configKey) ?? null;

  const apply = (v: SavedView): void => {
    setOpen(false);
    onApply(sanitizeConfig(v.config, spec, fallback));
    // No "View … applied." line (0.1.523, the success lines went app-wide): the sheet re-groups in
    // front of you and the Views menu marks the active view. `notify` now carries only refusals.
  };

  const save = async (): Promise<void> => {
    const trimmed = name.trim();
    if (trimmed === '' || busy) return;
    setBusy(true);
    setProblem(null);
    const r = await call('saveView', () => api.saveView.mutate({ screen, name: trimmed, config }));
    setBusy(false);
    if (r.ok) {
      setViews(r.data);
      setSaveOpen(false);
      setName('');
    } else {
      setProblem(r.failure.message);
    }
  };

  const remove = async (v: SavedView): Promise<void> => {
    setOpen(false);
    const ok = await ask({
      title: `Delete the view “${v.name}”?`,
      body: 'Only this saved view goes — the sheet, its records and your current view all stay.',
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;
    const r = await call('deleteSavedView', () => api.deleteSavedView.mutate({ id: v.id }));
    if (r.ok) {
      setViews({ available: r.data.available, views: r.data.views });
    } else {
      notify(r.failure.message);
    }
  };

  const openSave = (): void => {
    if (!enabled) return;
    if (views !== null && !available) {
      notify('Saved views need migration 081 on this database — your current view is kept on this device.');
      return;
    }
    setProblem(null);
    setSaveOpen(true);
  };

  const menu: ReactNode = enabled && available && (
    <div className="display-chip-wrap" ref={menuRef}>
      <button
        type="button"
        className={active === null ? 'display-bar-act' : 'display-bar-act display-bar-act-view'}
        aria-haspopup="menu"
        aria-expanded={open}
        // The accessible name STARTS with "Views" whether or not one is active — the specs and a
        // screen reader find the menu by that word; the active name follows it.
        aria-label={active === null ? 'Views' : `Views — ${active.name} is active`}
        onClick={() => setOpen((o) => !o)}
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <path
            d="M3.5 1.5 L10.5 1.5 L10.5 12.5 L7 9.8 L3.5 12.5 Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinejoin="round"
            fill={active === null ? 'none' : 'currentColor'}
          />
        </svg>
        {active === null ? 'Views' : active.name}
        {views !== null && views.views.length > 0 && <span className="display-bar-count">{views.views.length}</span>}
      </button>
      {open && (
        <div className="display-menu display-menu-views" role="menu" aria-label="Saved views">
          {views?.views.length === 0 && <span className="display-menu-note">No saved views yet.</span>}
          {views?.views.map((v) => (
            <div key={v.id} className="display-menu-viewrow">
              <button
                type="button"
                role="menuitemradio"
                aria-checked={active?.id === v.id}
                className="display-menu-row"
                onClick={() => apply(v)}
              >
                {v.name}
              </button>
              <button
                type="button"
                className="display-menu-x"
                aria-label={`Delete view ${v.name}`}
                onClick={() => void remove(v)}
              >
                ✕
              </button>
            </div>
          ))}
          <button
            type="button"
            role="menuitem"
            className="display-menu-row display-menu-dir"
            onClick={() => {
              setOpen(false);
              openSave();
            }}
          >
            Save current as…
          </button>
        </div>
      )}
    </div>
  );

  const sheet: ReactNode = (
    <>
      {confirmEl}
      {saveOpen && (
        <Sheet label="Save as view" onClose={() => setSaveOpen(false)} className="sheet-save-view">
          <form
            className="save-view-form"
            onSubmit={(e) => {
              e.preventDefault();
              void save();
            }}
          >
            <p className="section-head">Save as view</p>
            <p className="display-panel-note">
              Names this sheet's current grouping, order and sort for you. An existing name is overwritten.
            </p>
            <input
              className="save-view-name"
              aria-label="View name"
              placeholder="Fitting week"
              value={name}
              maxLength={60}
              disabled={busy}
              onChange={(e) => setName(e.target.value)}
            />
            {problem !== null && (
              <p role="alert" className="save-view-problem">
                {problem}
              </p>
            )}
            <div className="save-view-acts">
              <button type="button" className="chip" onClick={() => setSaveOpen(false)}>
                Cancel
              </button>
              <button type="submit" className="chip chip-primary" disabled={busy || name.trim() === ''}>
                Save
              </button>
            </div>
          </form>
        </Sheet>
      )}
    </>
  );

  return { menu, openSave, sheet };
}
